import React, { useState, useEffect } from 'react'
import { DatePicker, Select, Input, Row, Col, InputNumber, Cascader, message } from 'antd'
import { MinusOutlined } from '@ant-design/icons'
import { CommonSelector } from '@components'
import './style.less'
import { salesName, groupId, customersName } from '../CommonSelector/configs'
import { useKeyWordsList } from '@hooks'
import { CallEndReasonMap } from '@configs/map'
import { handleNumber, formatTime } from '@library'
import CompanySelector from '../CommonSelector/CompanySelector'
import { getAIQualityCheckPlanList } from '@services/ai-quality-check'
import { AIQualityCheckPlanListItem } from 'src/types/ai-quality-check'
import { KeyWordTypeEnum } from '@types'
import moment from 'moment'

interface RenderComponentProps {
  selectedKey?: string
  value?: string
  onChange?: (value, option?) => void
}

const { RangePicker } = DatePicker
const { Option } = Select
const operatorOptions = [
  { label: '>', value: 'gt' },
  { label: '≥', value: 'ge' },
  { label: '=', value: 'eq' },
  { label: '<', value: 'lt' },
  { label: '≤', value: 'le' },
]

const RenderComponent = ({ selectedKey, value, onChange }: RenderComponentProps) => {
  const [duration, setDuration] = useState([undefined, undefined])
  const [planList, setPlanList] = useState(undefined)
  const [aiScore, setAiScore] = useState([undefined, undefined])
  const [manualScore, setManualScore] = useState([undefined, undefined])
  const [aiQCHitRules, setAiQCHitRules] = useState([undefined, undefined])
  const { groupsList } = useKeyWordsList(true)

  useEffect(() => {
    if (selectedKey === 'duration' && Array.isArray(value)) {
      setDuration(value)
    } else {
      setDuration([undefined, undefined])
    }
  }, [selectedKey, value])

  useEffect(() => {
    if (selectedKey === 'aiQCPlanId' && !planList) {
      let isMounted = true
      const fetchData = async () => {
        const [res, err] = await getAIQualityCheckPlanList({})
        if (isMounted) {
          if (err) {
            message.error(`获取质检方案列表失败:${err?.message}`)
            return
          }
          const list = res?.list.map((item: AIQualityCheckPlanListItem) => ({
            label: item.name,
            value: item.id,
          })) || []
          setPlanList(list)
        }
      }
      fetchData()
      return () => {
        isMounted = false
      }
    }
  }, [selectedKey, planList])

  useEffect(() => {
    if (selectedKey === 'aiQCScore') {
      if (Array.isArray(value)) {
        setAiScore(value)
      } else {
        setAiScore([undefined, undefined])
      }
    }
  }, [selectedKey, value])

  useEffect(() => {
    if (selectedKey === 'qualityCheckScore') {
      if (Array.isArray(value)) {
        setManualScore(value)
      } else {
        setManualScore([undefined, undefined])
      }
    }
  }, [selectedKey, value])

  useEffect(() => {
    if (selectedKey === 'aiQCHitRules') {
      if (Array.isArray(value)) {
        setAiQCHitRules(value)
      } else {
        setAiQCHitRules([undefined, undefined])
      }
    }
  }, [selectedKey, value])

  let ele = null

  switch (selectedKey) {
    case 'salesName':
      ele = (
        <CommonSelector
          {...salesName}
          value={value}
          onChange={(v, option) => onChange(v, option)}
          mode="multiple" />
      )
      break
    case 'cid':
      ele = (
        <Select
          allowClear
          value={value}
          onChange={(v, option) => onChange(v, option)}
          mode="tags"
          placeholder="请输入或直接粘贴多条数据，会自动分割"
          tokenSeparators={[',', '，', ' ', '\t', '\n', '\r']}
          notFoundContent={<span>多条数据以空格、换行或逗号分割</span>}
          maxTagCount={5}
        />
      )
      break
    case 'callNumber':
      ele = (
        <Input
          addonBefore="+86"
          value={value}
          onChange={(e) => {
            onChange(e.target.value)
          }}
        />
      )
      break
    case 'phone':
      ele = (
        <Input
          addonBefore="+86"
          value={value}
          onChange={(e) => {
            onChange(e.target.value)
          }}
        />
      )
      break
    case 'callBeginTime':
      ele = (
        <RangePicker
          value={value ? [moment(value[0]), moment(value[1])] : undefined}
          onChange={(v, option) => {
            onChange(v?.length === 2 ? [formatTime(v[0], 'start'), formatTime(v[1], 'end')] : undefined, option)
          }}
        />
      )
      break
    case 'manualQCTime':
      ele = (
        <RangePicker
          value={value ? [moment(value[0]), moment(value[1])] : undefined}
          onChange={(v, option) => {
            onChange(v?.length === 2 ? [formatTime(v[0], 'start'), formatTime(v[1], 'end')] : undefined, option)
          }}
        />
      )
      break
    case 'aiQCTime':
      ele = (
        <RangePicker
          value={value ? [moment(value[0]), moment(value[1])] : undefined}
          onChange={(v, option) => {
            onChange(v?.length === 2 ? [formatTime(v[0], 'start'), formatTime(v[1], 'end')] : undefined, option)
          }}
        />
      )
      break
    case 'duration':
      ele = (
        <Row justify="space-between" align="middle">
          <Col span={10}>
            <InputNumber
              style={{ width: '100%' }}
              value={duration[0]}
              onChange={(val) => {
                const newState = [val, duration[1]]
                setDuration(newState)
                onChange(newState)
              }}
            />
          </Col>
          <Col span={1}>
            <MinusOutlined />
          </Col>
          <Col span={10}>
            <InputNumber
              style={{ width: '100%' }}
              value={duration[1]}
              min={duration[0]}
              onChange={(val) => {
                const newState = [duration[0], val]
                setDuration(newState)
                onChange(newState)
              }}
            />
          </Col>
        </Row>
      )
      break
    case 'saleKeyWord':
      ele = (<Cascader
        value={Array.isArray(value) ? value : undefined}
        style={{ width: '300px' }}
        className="key-word-cascader"
        options={groupsList[KeyWordTypeEnum.customerService]}
        expandTrigger="hover"
        onChange={(v, option) => onChange(v, option)}
        multiple
        maxTagCount="responsive"
        placeholder="未筛选则默认全部"
        fieldNames={{
          children: 'list',
        }}
      />)
      break
    case 'customerKeyWord':
      ele = (<Cascader
        value={Array.isArray(value) ? value : undefined}
        style={{ width: '300px' }}
        className="key-word-cascader"
        options={groupsList[KeyWordTypeEnum.client]}
        expandTrigger="hover"
        onChange={(v, option) => onChange(v, option)}
        multiple
        maxTagCount="responsive"
        placeholder="未筛选则默认全部"
        fieldNames={{
          children: 'list',
        }}
      />)
      break
    case 'aiQCPlanId':
      ele = (
        <Select
          allowClear
          options={planList}
          value={value}
          onChange={(v, option) => {
            onChange(v, option)
          }}
          mode="multiple"
        />
      )
      break
    case 'appealStatus': {
      const options = [
        {
          'label': '未申诉',
          'value': 0,
        },
        {
          'label': '待复核',
          'value': 1,
        },
        {
          'label': '已复核',
          'value': 2,
        },
      ]
      ele = (
        <Select
          allowClear
          value={value}
          options={options}
          onChange={(v, option) => onChange(v, option)}
          mode="multiple"
          placeholder="未筛选则默认全部"
        />
      )
    }
      break
    case 'aiQCScore':
      ele = (
        <Row justify="space-between" align="middle">
          <Col span={10}>
            <Select placeholder="</=/>" allowClear options={operatorOptions} value={aiScore[0]} onChange={(val, option) => {
              const newState: [string | undefined, number] = [val, aiScore[1]]
              setAiScore(newState)
              onChange(newState, option)
            }} />
          </Col>
          <Col span={1}>
            <MinusOutlined />
          </Col>
          <Col span={10}>
            <InputNumber value={aiScore[1]} min={0} max={99999999999} onChange={(val) => {
              const newState: [string | undefined, number] = [aiScore[0], val]
              setAiScore(newState)
              onChange(newState)
            }} />
          </Col>
        </Row>
      )
      break
    case 'qualityCheckScore':
      ele = (
        <Row justify="space-between" align="middle">
          <Col span={10}>
            <Select placeholder="</=/>" allowClear options={operatorOptions} value={manualScore[0]} onChange={(val, option) => {
              const newState: [string | undefined, number] = [val, manualScore[1]]
              setManualScore(newState)
              onChange(newState, option)
            }} />
          </Col>
          <Col span={1}>
            <MinusOutlined />
          </Col>
          <Col span={10}>
            <InputNumber value={manualScore[1]} min={0} max={99999999999} onChange={(val) => {
              const newState: [string | undefined, number] = [manualScore[0], val]
              setManualScore(newState)
              onChange(newState)
            }} />
          </Col>
        </Row>
      )
      break
    case 'aiQCHitRules':
      ele = (
        <Row justify="space-between" align="middle">
          <Col span={10}>
            <Select placeholder="</=/>" allowClear options={operatorOptions} value={aiQCHitRules[0]} onChange={(val, option) => {
              const newState: [string | undefined, number] = [val, aiQCHitRules[1]]
              setAiQCHitRules(newState)
              onChange(newState, option)
            }} />
          </Col>
          <Col span={1}>
            <MinusOutlined />
          </Col>
          <Col span={10}>
            <InputNumber value={aiQCHitRules[1]} min={0} max={99999999999} onChange={(val) => {
              const newState: [string | undefined, number] = [aiQCHitRules[0], val]
              setAiQCHitRules(newState)
              onChange(newState)
            }} />
          </Col>
        </Row>
      )
      break
    case 'companyId':
      ele = (
        <CompanySelector value={value}
          onChange={(v, option) => onChange(v, option)} />
      )
      break
    case 'customerName':
      ele = (
        <CommonSelector
          {...customersName}
          value={value}
          onChange={(v, option) => onChange(v, option)}
          placeholder="请选择客户名称"
        />
      )
      break
    case 'callType': {
      const options = [
        {
          'label': '点击外呼',
          'value': 1,
        },
        {
          'label': '拨打外呼',
          'value': 2,
        },
      ]
      ele = (
        <Select
          allowClear
          options={options}
          value={value}
          onChange={(v, option) => onChange(v, option)}
          placeholder="请选择外呼类型"
        />
      )
    }
      break
    case 'callResult':
      ele = (
        <Select allowClear onChange={(v, option) => onChange(v, option)}>
          {Object.entries(CallEndReasonMap).map(([value, text]) => {
            const val = handleNumber(value)
            return <Option key={value} value={val}>{text}</Option>
          })}
        </Select>
      )
      break
    case 'matchKeywordStatus': {
      const options = [
        {
          'label': '命中',
          'value': 2,
        },
        {
          'label': '未命中',
          'value': 3,
        },
      ]
      ele = (
        <Select
          allowClear
          options={options}
          value={value}
          onChange={(v, option) => onChange(v, option)}
          placeholder="请选择关键词匹配状态"
        />
      )
    }
      break
    case 'groupId':
      ele = (
        <CommonSelector
          {...groupId}
          value={value}
          onChange={(v, option) => onChange(v, option)}
          mode="multiple"
        />
      )
      break
    case 'qualityCheckSalesName':
      ele = (
        <CommonSelector
          {...salesName}
          value={value}
          onChange={(v, option) => onChange(v, option)}
        />
      )
      break
    case 'manualQCSalesGroupId':
      ele = (
        <CommonSelector
          {...groupId}
          value={value}
          onChange={(v, option) => onChange(v, option)}
          mode="multiple"
        />
      )
      break
    case 'qualityCheckStatus': {
      const options = [
        {
          'label': '已打分',
          'value': 1,
        },
        {
          'label': '未打分',
          'value': 0,
        },
      ]
      ele = (
        <Select
          allowClear
          value={value}
          options={options}
          onChange={(v, option) => onChange(v, option)}
          placeholder="请选择人工质检状态"
        />
      )
    }
      break
    case 'aiQCStatus': {
      const options = [
        {
          'label': '待质检',
          'value': 0,
        },
        {
          'label': '已质检',
          'value': 1,
        },
      ]
      ele = (
        <Select
          allowClear
          options={options}
          value={value}
          onChange={(v, option) => onChange(v, option)}
          placeholder="请选择AI质检状态"
        />
      )
    }
      break
  }
  return (
    ele
  )
}

export default RenderComponent
